// src/features/clients/pages/LatestVisitsPage.tsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import useDateFormatting from '../../../hooks/useDateFormatting';
import Layout from '../../../components/layout/Layout';
import PaginationControls from '../../../components/common/PaginationControls';
import ExportExcelButton from '../../../components/common/ExportExcelButton';
import { History, CalendarClock, MessageSquare, Building, Loader2, DollarSign, Ban } from 'lucide-react';
import ExportMovementsModal from '../components/ExportMovementsModal';
import CancelVisitModal from '../components/CancelVisitModal';
import { toast } from 'react-hot-toast';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import SearchInput from '../../../components/common/SearchInput';
import DateTimeDisplay from '../../../components/common/DateTimeDisplay';
import { clientsApi } from '../../../api/clientsApi';

// --- Constants ---
const VISITS_PER_PAGE = 20;

// --- Interfaces ---
interface VisitData {
  id: string;
  clientId: string;
  clientName: string | null;
  clientPhone: string;
  checkInDate: string; 
  branchId?: string;
  branchName?: string;
  feedback?: string;
  countryCode?: string;
  points?: number;
  earnedPoints?: number; // ✅ Cashback/puntos ganados en esta visita
  amount?: number | null; // monto de la venta
  ticketRef?: string;     // folio del ticket
  userName?: string;      // quién la registró
  cancelledAt?: string;   // compra cancelada
  cancelReason?: string;
}

interface DailyVisits {
  date: string;
  dateObj: Date;
  visits: VisitData[];
  count: number;
}

// --- HELPER FUNCTIONS ---

const formatLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateDisplayForGroupHeader = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  const formattedDate = date.toLocaleDateString('es-ES', options);
  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
};

const getFeedbackColor = (f?: string): string => {
  const feedbackValue = f || 'omitted';
  switch (feedbackValue) {
    case 'excelente': return 'text-yellow-500';
    case 'bueno': return 'text-green-500';
    case 'neutral': return 'text-blue-500';
    case 'malo': return 'text-red-500';
    default: return 'text-content-3';
  }
};

const getFeedbackText = (f?: string): string => {
  const feedbackValue = f || 'omitted';
  switch (feedbackValue) {
    case 'excelente': return 'Excelente';
    case 'bueno': return 'Buena';
    case 'neutral': return 'Neutral';
    case 'malo': return 'Mala';
    default: return 'Omitido';
  }
};

// Venta · folio · quién atendió (Bramarket 10-oct-2026). Vacío si la visita no trae ninguno.
const VisitDetail: React.FC<{ visit: VisitData; className?: string }> = ({ visit, className = '' }) => {
  const partes: string[] = [];
  if (visit.amount != null && visit.amount > 0) partes.push(`Venta $${visit.amount.toFixed(2)}`);
  if (visit.ticketRef) partes.push(`Folio ${visit.ticketRef}`);
  if (visit.userName) partes.push(`Atendió ${visit.userName}`);
  if (!partes.length && !visit.cancelledAt) return null;
  return (
    <div className={`text-xs text-content-3 ${className}`} data-testid="visita-detalle">
      {visit.cancelledAt && (
        <span className="mr-1 inline-block rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-700 dark:bg-red-500/20 dark:text-red-300" data-testid="visita-cancelada" title={visit.cancelReason}>
          Cancelada{visit.cancelReason ? `: ${visit.cancelReason}` : ''}
        </span>
      )}
      {partes.join(' · ')}
    </div>
  );
};

// Botón «Cancelar compra» (dueño/gerente) — solo en compras vivas.
const CancelButton: React.FC<{ visit: VisitData; onCancel?: (v: VisitData) => void }> = ({ visit, onCancel }) => {
  if (!onCancel || visit.cancelledAt) return null;
  return (
    <button type="button" title="Cancelar compra" data-testid="btn-cancelar-compra"
      onClick={(e) => { e.stopPropagation(); onCancel(visit); }}
      className="rounded p-1 text-content-3 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10">
      <Ban className="h-4 w-4" />
    </button>
  );
};

// --- VisitRow component ---
interface VisitRowProps {
  visit: VisitData;
  timeZoneInfo: ReturnType<typeof useDateFormatting>['timeZoneInfo'];
  onNavigateClient: (clientId: string) => void;
  showBranchColumn: boolean;
  isMobile?: boolean;
  isCashback?: boolean; // ✅ AGREGADO: Mostrar columna de cashback ganado
  onCancel?: (v: VisitData) => void; // dueño/gerente: cancelar compra
}

const VisitRow: React.FC<VisitRowProps> = React.memo(({
  visit, timeZoneInfo, onNavigateClient, showBranchColumn, isMobile = false, isCashback = false, onCancel
}) => {
  const checkInDate = new Date(visit.checkInDate);
  const earned = visit.earnedPoints ?? 0;

  if (isMobile) {
    return (
      <div className="border rounded-lg p-3 mb-3 hover:bg-surface-2 animate-fade-in-up" style={{ animationDelay: '0.1s' }} onClick={() => onNavigateClient(visit.clientId)}>
        <div className="flex justify-between items-start">
          <div className="flex-1 mr-2">
            <ClientContactInfo
              clientId={visit.clientId}
              clientName={visit.clientName}
              phoneNumber={visit.clientPhone}
              countryCode={visit.countryCode}
              iconSize="sm"
              nameAsLink={!!onNavigateClient}
            />
          </div>
          {visit.feedback && (
            <div className="flex items-center flex-shrink-0">
              <MessageSquare className={`h-4 w-4 mr-1 ${getFeedbackColor(visit.feedback)}`} />
              <span className={`text-xs font-medium ${getFeedbackColor(visit.feedback)}`}>
                {getFeedbackText(visit.feedback)}
              </span>
            </div>
          )}
        </div>
        <VisitDetail visit={visit} className="mt-1" />
        {/* ✅ Cashback ganado en móvil */}
        {isCashback && (
          <div className="flex justify-end mt-1">
            <span className={`text-xs font-semibold ${visit.cancelledAt ? 'line-through text-content-3' : earned > 0 ? 'text-green-600 dark:text-green-400' : 'text-content-3'}`}>
              {earned > 0 ? `+$${earned.toFixed(2)}` : 'Sin cashback'}
            </span>
          </div>
        )}
        <div className="flex justify-end"><CancelButton visit={visit} onCancel={onCancel} /></div>
        <div className="flex justify-between items-center mt-2 pt-2 border-t border-line">
          <div className="flex items-center space-x-1 text-xs text-content-3">
            <CalendarClock className="h-3 w-3 text-content-3" />
            <DateTimeDisplay
              date={checkInDate}
              format="dateTime"
              compact={true}
              useBusinessTZ={true}
              customTimeZone={timeZoneInfo.business.zone}
            />
          </div>
          {showBranchColumn && (
            <div className="text-xs text-content-3 flex items-center truncate" title={visit.branchName || 'Principal'}>
              <Building className="h-3 w-3 mr-1 text-content-3 flex-shrink-0" />
              {visit.branchName || 'Principal'}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <tr className="hover:bg-surface-2 animate-fade-in-up transition-all duration-150">
      <td className="px-6 py-4 whitespace-nowrap">
        <ClientContactInfo
          clientId={visit.clientId}
          clientName={visit.clientName}
          phoneNumber={visit.clientPhone}
          countryCode={visit.countryCode}
          nameAsLink={!!onNavigateClient}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center text-sm text-content-2">
          <CalendarClock className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" />
          <DateTimeDisplay
            date={checkInDate}
            format="dateTime"
            useBusinessTZ={true}
            customTimeZone={timeZoneInfo.business.zone}
          />
          {/* Aquí colgaba `timeZoneInfo.client.abbreviation` entre paréntesis. NUNCA se
              pintó: `useDateFormatting` devuelve {business, user, difference, isDifferent,
              differenceText} — no hay rama `client` ni ninguna `abbreviation`, y tampoco
              existe la zona horaria del cliente en el modelo. Se quita el condicional
              muerto; la hora ya sale en la zona del negocio, que es la que importa. */}
        </div>
        <VisitDetail visit={visit} className="mt-1 pl-[22px] whitespace-normal max-w-[260px]" />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {visit.feedback && <MessageSquare className={`h-4 w-4 mr-1.5 flex-shrink-0 ${getFeedbackColor(visit.feedback)}`} />}
          <span className={`text-sm ${getFeedbackColor(visit.feedback)}`}> {getFeedbackText(visit.feedback)} </span>
        </div>
      </td>
      {/* ✅ Columna de cashback ganado - solo visible en modo cashback */}
      {isCashback && (
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <span className={`text-sm font-medium ${visit.cancelledAt ? 'line-through text-content-3' : earned > 0 ? 'text-green-600 dark:text-green-400' : 'text-content-3'}`}>
            {earned > 0 ? (
              <span className={`inline-flex items-center ${visit.cancelledAt ? 'line-through' : ''}`}>
                <DollarSign className="h-3.5 w-3.5 mr-0.5" />
                +{earned.toFixed(2)}
              </span>
            ) : (
              <span className="text-xs">Sin cashback</span>
            )}
          </span>
        </td>
      )}
      {showBranchColumn && (
        <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
          <div className="flex items-center">
            <Building className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" />
            {visit.branchName || 'Principal'}
          </div>
        </td>
      )}
      <td className="px-2 py-4 whitespace-nowrap text-right"><CancelButton visit={visit} onCancel={onCancel} /></td>
    </tr>
  );
});
VisitRow.displayName = 'VisitRow';

// --- MAIN COMPONENT ---

const LatestVisitsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addError, clearErrors } = useError();
  const { timeZoneInfo } = useDateFormatting();
  const contentTopRef = useRef<HTMLDivElement>(null);

  // State
  const [visits, setVisits] = useState<VisitData[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageChanging, setPageChanging] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [totalHits, setTotalHits] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // ✅ Detectar modo de lealtad
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  const isCashback = loyaltyMode === 'cashback';

  // Sucursales del negocio: vienen ya de la sesión de base (currentUser.branches), no de Firestore.
  // La columna de sucursal solo tiene sentido en negocios multi-sucursal.
  const branches = currentUser?.branches || [];

  // --- Fetch Visits ---
  const fetchVisits = useCallback(async (page: number, term: string = '') => {
      if (!currentUser?.businessId) return;
      
      const isPageChange = currentPageNum !== page && visits.length > 0;
      if (isPageChange) setPageChanging(true);
      else setLoading(true);
      
      if (clearErrors) clearErrors();

      try {
          const { visits: mappedVisits, total } = await clientsApi.recentVisits({ term, page, size: VISITS_PER_PAGE });

          setVisits(mappedVisits);
          setTotalHits(total);
          setCurrentPageNum(page);

      } catch (error) {
          console.error("Error fetching visits:", error);
          if (addError) addError("Error al cargar visitas.", undefined, "fetchVisits");
      } finally {
          setLoading(false);
          setPageChanging(false);
      }
  }, [currentUser?.businessId, currentPageNum, addError, visits.length, clearErrors]);

  useEffect(() => {
      if (currentUser?.businessId) {
          fetchVisits(1, '');
      }
  }, [currentUser?.businessId]); 

  useEffect(() => {
    if (contentTopRef.current) contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPageNum]);

  // CORRECCIÓN EN EL GROUPING
  const groupedVisits = useMemo<DailyVisits[]>(() => {
    if (!visits.length) return [];
    const groups: Record<string, { visits: VisitData[], representativeDate: Date }> = {};

    visits.forEach(visit => {
      const dateObj = new Date(visit.checkInDate);
      if (!isNaN(dateObj.getTime())) {
        const dateKey = formatLocalDateKey(dateObj); 
        
        if (!groups[dateKey]) groups[dateKey] = { visits: [], representativeDate: dateObj };
        groups[dateKey].visits.push(visit);
      }
    });

    return Object.entries(groups)
      .map(([_, groupData]) => ({
        date: formatDateDisplayForGroupHeader(groupData.representativeDate),
        dateObj: groupData.representativeDate,
        visits: groupData.visits,
        count: groupData.visits.length
      }))
      .sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());
  }, [visits]);

  const handleNavigateClient = useCallback((clientId: string) => {
    if (clientId) navigate(`/dashboard/clients/${clientId}/history`);
  }, [navigate]);

  const handleSearch = (term: string | null) => {
      const newTerm = term || '';
      setSearchTerm(newTerm);
      fetchVisits(1, newTerm);
  };

  const handleNextPage = () => {
      if (currentPageNum * VISITS_PER_PAGE < totalHits && !loading) {
          fetchVisits(currentPageNum + 1, searchTerm);
      }
  };

  const handlePrevPage = () => {
      if (currentPageNum > 1 && !loading) {
          fetchVisits(currentPageNum - 1, searchTerm);
      }
  };

  // Export completo (servidor). El botón ya se oculta a quien no es dueño y pide PRO (ExportExcelButton).
  const [exportOpen, setExportOpen] = useState(false);
  // Cancelar compra: dueño y gerente (el servidor también lo exige; el cajero no).
  const canCancel = currentUser?.baseRole === 'owner' || currentUser?.baseRole === 'manager';
  const [cancelVisit, setCancelVisit] = useState<VisitData | null>(null);
  const onCancel = canCancel ? (v: VisitData) => setCancelVisit(v) : undefined;

  const showBranchColumn = branches.length > 1;
  const canGoPrev = currentPageNum > 1;
  const canGoNext = currentPageNum * VISITS_PER_PAGE < totalHits;

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        <div ref={contentTopRef}></div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up">
          <div>
              <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center"><History className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" /> Últimas Visitas</h1>
              <p className="mt-1 text-sm text-content-2">Registro cronológico de las visitas.</p>
          </div>
          <ExportExcelButton
             onExport={() => setExportOpen(true)}
             filename="movimientos.xlsx"
             className="animate-fade-in-up"
          />
        </div>

        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up">
          <div className="px-4 py-5 sm:p-6">
            
            <div className="mb-4">
                <SearchInput 
                    onSearch={handleSearch} 
                    placeholder="Buscar por nombre, teléfono o sucursal" 
                    className="max-w-lg" 
                    disabled={loading}
                />
            </div>

            <div className="hidden md:block">
              {loading && visits.length === 0 ? (
                 <div className="py-12 text-center text-content-3">Cargando visitas...</div>
              ) : groupedVisits.length === 0 ? (
                 <div className="py-12 text-center text-sm text-content-3">
                    {searchTerm ? `No se encontraron visitas para "${searchTerm}".` : 'No hay visitas registradas.'}
                 </div>
              ) : (
                <div className="space-y-8">
                  {groupedVisits.map((group) => (
                    <div key={group.date}>
                      <div className="flex items-center mb-3"><h3 className="text-lg font-medium text-content">{group.date}</h3></div>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-line">
                          <tbody className="bg-surface divide-y divide-line">
                            {group.visits.map((visit) => (
                              <VisitRow key={visit.id} visit={visit} timeZoneInfo={timeZoneInfo} onNavigateClient={handleNavigateClient} showBranchColumn={showBranchColumn} isCashback={isCashback} onCancel={onCancel} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="md:hidden space-y-6">
                {groupedVisits.map((group) => (
                    <div key={group.date}>
                        <h3 className="text-base font-medium text-content mb-2">{group.date}</h3>
                        <div className="space-y-3">
                            {group.visits.map((visit) => (
                                <VisitRow key={visit.id} visit={visit} timeZoneInfo={timeZoneInfo} onNavigateClient={handleNavigateClient} showBranchColumn={showBranchColumn} isMobile={true} isCashback={isCashback} onCancel={onCancel} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {totalHits > VISITS_PER_PAGE && (
                <PaginationControls
                    currentPage={currentPageNum}
                    isLoading={loading || pageChanging}
                    onPrevPage={handlePrevPage}
                    onNextPage={handleNextPage}
                    canGoPrev={canGoPrev}
                    canGoNext={canGoNext}
                    itemsPerPage={VISITS_PER_PAGE}
                    countOnPage={visits.length}
                    totalItems={totalHits}
                />
            )}
          </div>
        </div>
      </div>
      
      {cancelVisit && (
        <CancelVisitModal
          visit={{
            id: cancelVisit.id, clientName: cancelVisit.clientName, amount: cancelVisit.amount, ticketRef: cancelVisit.ticketRef,
            earnedPoints: cancelVisit.earnedPoints ?? 0,
            dateLabel: new Date(cancelVisit.checkInDate).toLocaleString('es-MX', { timeZone: timeZoneInfo.business.zone, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
          }}
          isCashback={isCashback}
          onClose={() => setCancelVisit(null)}
          onCancelled={(r) => {
            setCancelVisit(null);
            toast.success(r.negative
              ? `Compra cancelada. Su saldo quedó en ${isCashback ? '$' : ''}${r.balance.toFixed(2)} (negativo).`
              : 'Compra cancelada.');
            fetchVisits(currentPageNum, searchTerm);
          }}
        />
      )}

      {exportOpen && (
        <ExportMovementsModal
          timeZone={timeZoneInfo.business.zone}
          branches={branches.filter((b) => b.isActive !== false)}
          onClose={() => setExportOpen(false)}
        />
      )}

      {pageChanging && (
        <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50">
          <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
          <span className="text-sm font-medium text-content-2">Cargando...</span>
        </div>
      )}
    </Layout>
  );
};

export default LatestVisitsPage;